import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  ShieldAlert,
  Target,
  BarChart3,
  Coins,
  FlaskConical,
  Info,
  Activity,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const CAPITAL_EFFICIENCY_IMG =
  'https://customer-assets-wrfwihn1.emergentagent.net/job_d55a5905-f2f9-4823-ae56-0e4b4516ff30/artifacts/vm1fyabs_BTC%20annualized%20return.webp';
const METRICS_IMG =
  'https://customer-assets-wrfwihn1.emergentagent.net/job_d55a5905-f2f9-4823-ae56-0e4b4516ff30/artifacts/21juu06z_BTC%20metrics.webp';

const CONTENT = {
  el: {
    breadcrumb_home: 'Αρχική',
    breadcrumb_features: 'Χαρακτηριστικά',
    title: 'Ανάλυση Ρίσκου και Συμπεριφοράς',
    subtitle:
      'Η επιστημονική αξιολόγηση ενός αλγορίθμου υπερβαίνει την απλή μέτρηση της απόδοσης',
    kicker: 'Ποσοτική ανάλυση & Backtesting',
    sections: [
      {
        icon: 'intro',
        heading: '1. Εισαγωγή',
        body: [
          'Στην ποσοτική χρηματοοικονομική ανάλυση, η ποιότητα ενός αλγορίθμου δεν αξιολογείται αποκλειστικά από το συνολικό κέρδος που μπορεί να παράγει. Εξίσου σημαντικοί παράγοντες είναι η σταθερότητα των αποτελεσμάτων, η συνέπεια των σημάτων, η προσαρμοστικότητα σε διαφορετικά καθεστώτα αγοράς και η αποτελεσματική διαχείριση του επενδυτικού κινδύνου.',
          'Ο Momentum Volatility ST Indicator αναπτύχθηκε με στόχο τη συστηματική αναγνώριση υψηλής ποιότητας τάσεων μέσω συνδυασμού μαθηματικής ορμής, μεταβλητότητας και στατιστικής ανάλυσης. Η αξιολόγησή του βασίζεται σε εκτεταμένα ιστορικά backtests μεγάλης χρονικής διάρκειας, τα οποία εξετάζουν όχι μόνο την κερδοφορία αλλά και τη συνολική συμπεριφορά της στρατηγικής υπό διαφορετικές συνθήκες αγοράς.',
          'Η φιλοσοφία του Heliophant στηρίζεται στην αρχή ότι ένας αξιόπιστος αλγόριθμος οφείλει να χαρακτηρίζεται από συνέπεια, επαναληψιμότητα και πειθαρχημένη διαχείριση κινδύνου, και όχι αποκλειστικά από υψηλές αποδόσεις σε μεμονωμένες χρονικές περιόδους.',
        ],
      },
      {
        icon: 'btc',
        heading: '2. Bitcoin (BTC/USD)',
        subheading: 'Περίοδος αξιολόγησης',
        highlight: '5 Οκτωβρίου 2009 – 17 Ιουλίου 2026',
        body: [
          'Η αξιολόγηση πραγματοποιήθηκε σε ιστορικό δείγμα μεγαλύτερο των 16 ετών, το οποίο περιλαμβάνει όλους τους κύριους ανοδικούς και καθοδικούς κύκλους της αγοράς Bitcoin, περιόδους εξαιρετικά υψηλής μεταβλητότητας, καθώς και διαφορετικά μακροοικονομικά περιβάλλοντα. Η μεγάλη διάρκεια του δείγματος επιτρέπει την αντικειμενική αξιολόγηση της ανθεκτικότητας και της στατιστικής αξιοπιστίας του αλγορίθμου.',
        ],
      },
      {
        icon: 'metrics',
        heading: '3. Key Performance Metrics',
        table: {
          headers: ['Μετρική', 'Αποτέλεσμα'],
          rows: [
            ['Annualized Return (CAGR)', '369.73%'],
            ['Winning Trades', '66.7%'],
            ['Total Trades', '69'],
            ['Average Trade Duration', '81 Days'],
          ],
        },
        note: 'Οι παρακάτω εικόνες αντιστοιχούν σε ιστορική προσομοίωση με αρχικό κεφάλαιο 10.000 USD και σύνθετη κεφαλαιοποίηση (compounding).',
        images: [
          {
            src: CAPITAL_EFFICIENCY_IMG,
            alt: 'BTC/USD — Capital efficiency: Annualized return (CAGR) — All 369.73%, Long 352.70%, Short 348.63%',
            caption:
              'BTC/USD · Capital efficiency — Annualized return (CAGR) All / Long / Short',
          },
          {
            src: METRICS_IMG,
            alt: 'BTC/USD — Return details, Trades analysis, Equity run-ups & drawdowns (initial capital $10K, compounding)',
            caption:
              'BTC/USD · Πλήρες panel μετρικών backtest — Return details, Trades analysis, Equity run-ups & drawdowns',
          },
        ],
      },
      {
        icon: 'interpretation',
        heading: '4. Ερμηνεία των αποτελεσμάτων',
        body: [
          'Ο Μέσος Ετήσιος Σύνθετος Ρυθμός Απόδοσης (Compound Annual Growth Rate – CAGR) ανέρχεται σε 369,73%, εκφράζοντας τον θεωρητικό μέσο ετήσιο ρυθμό ανάπτυξης του κεφαλαίου κατά την εξεταζόμενη περίοδο, λαμβάνοντας υπόψη τη σύνθετη κεφαλαιοποίηση (compound growth). Το CAGR αποτελεί έναν από τους πλέον αναγνωρισμένους δείκτες αξιολόγησης επενδυτικών στρατηγικών, καθώς επιτρέπει τη συγκρίσιμη αποτίμηση της απόδοσης ανεξάρτητα από τη διάρκεια του backtest.',
          'Παράλληλα, ο αλγόριθμος ολοκλήρωσε 69 συνολικές συναλλαγές, εκ των οποίων το 66,7% ήταν κερδοφόρες. Το ποσοστό αυτό υποδηλώνει υψηλή ικανότητα αναγνώρισης των κυρίαρχων τάσεων της αγοράς και περιορισμό των ψευδών σημάτων.',
          'Η μέση διάρκεια συναλλαγής των 81 ημερών επιβεβαιώνει ότι η στρατηγική δεν στοχεύει στη συχνή εναλλαγή θέσεων ή στο βραχυπρόθεσμο trading υψηλής συχνότητας. Αντίθετα, ο Momentum Volatility ST Indicator έχει σχεδιαστεί ώστε να αναγνωρίζει και να ακολουθεί τις σημαντικές μεσοπρόθεσμες και μακροπρόθεσμες κινήσεις της αγοράς, επιτρέποντας στις ισχυρές τάσεις να εξελιχθούν πριν παραχθεί νέο σήμα.',
        ],
        metricCards: [
          { label: 'Annualized Return (CAGR)', value: '369.73%', accent: 'emerald' },
          { label: 'Winning Trades', value: '66.7%', accent: 'cyan' },
          { label: 'Total Trades', value: '69', accent: 'sky' },
          { label: 'Average Trade Duration', value: '81 Days', accent: 'violet' },
        ],
      },
      {
        icon: 'methodology',
        heading: '5. Επιστημονική προσέγγιση αξιολόγησης',
        body: [
          'Η αξιολόγηση του Momentum Volatility ST Indicator βασίζεται σε αντικειμενικά ποσοτικά κριτήρια και όχι σε μεμονωμένες επιτυχημένες συναλλαγές ή βραχυχρόνιες περιόδους υψηλής απόδοσης. Η χρήση πολυετών ιστορικών δεδομένων, σε συνδυασμό με στατιστικούς δείκτες αξιοπιστίας και διαχείρισης κινδύνου, επιτρέπει την εκτίμηση της σταθερότητας, της συνέπειας και της προσαρμοστικότητας του αλγορίθμου σε διαφορετικά καθεστώτα αγοράς.',
          'Η φιλοσοφία του Heliophant είναι ότι η μακροχρόνια επιτυχία δεν προκύπτει από την πρόβλεψη κάθε επιμέρους κίνησης της αγοράς, αλλά από τη συστηματική εφαρμογή μιας πειθαρχημένης και επιστημονικά τεκμηριωμένης μεθοδολογίας, η οποία επιδιώκει τη βελτιστοποίηση της σχέσης μεταξύ απόδοσης και αναλαμβανόμενου κινδύνου.',
        ],
      },
      {
        icon: 'note',
        heading: '6. Σημείωση',
        body: [
          'Οι παραπάνω μετρήσεις προέρχονται από ιστορικές προσομοιώσεις (backtesting) και χρησιμοποιούνται αποκλειστικά για την αξιολόγηση της στατιστικής συμπεριφοράς του αλγορίθμου. Οι ιστορικές επιδόσεις δεν αποτελούν εγγύηση αντίστοιχων μελλοντικών αποτελεσμάτων, καθώς η συμπεριφορά των χρηματοοικονομικών αγορών επηρεάζεται από παράγοντες που μεταβάλλονται διαχρονικά.',
        ],
      },
    ],
    ctaTitle: 'Δες τα σήματα σε πραγματικές συνθήκες',
    ctaDesc:
      'Ο Momentum Volatility ST Indicator διατίθεται στους συνδρομητές του Basic Plan μέσω TradingView.',
    ctaBtn: 'Απόκτησε πρόσβαση',
  },
  en: {
    breadcrumb_home: 'Home',
    breadcrumb_features: 'Features',
    title: 'Risk & Behavior Analysis',
    subtitle:
      'The scientific evaluation of an algorithm extends beyond a simple measurement of returns',
    kicker: 'Quantitative Analysis & Backtesting',
    sections: [
      {
        icon: 'intro',
        heading: '1. Introduction',
        body: [
          'In quantitative finance, the quality of an algorithm is not judged solely by the total profit it can produce. Equally important factors are the stability of results, the consistency of signals, adaptability across different market regimes, and effective investment-risk management.',
          'The Momentum Volatility ST Indicator was developed to systematically identify high-quality trends by combining mathematical momentum, volatility, and statistical analysis. Its evaluation is based on extensive long-horizon historical backtests that examine not only profitability but also the overall behavior of the strategy under different market conditions.',
          'The Heliophant philosophy is that a reliable algorithm must be characterized by consistency, repeatability, and disciplined risk management — not by outsized returns concentrated in isolated periods.',
        ],
      },
      {
        icon: 'btc',
        heading: '2. Bitcoin (BTC/USD)',
        subheading: 'Evaluation period',
        highlight: 'October 5, 2009 — July 17, 2026',
        body: [
          'The evaluation was performed on a historical sample longer than 16 years, covering all major bull and bear cycles of the Bitcoin market, periods of exceptionally high volatility, and a variety of macroeconomic environments. The length of the sample allows an objective assessment of the algorithm\u2019s robustness and statistical reliability.',
        ],
      },
      {
        icon: 'metrics',
        heading: '3. Key Performance Metrics',
        table: {
          headers: ['Metric', 'Result'],
          rows: [
            ['Annualized Return (CAGR)', '369.73%'],
            ['Winning Trades', '66.7%'],
            ['Total Trades', '69'],
            ['Average Trade Duration', '81 Days'],
          ],
        },
        note: 'The images below correspond to a historical simulation with an initial capital of USD 10,000 and compound growth (compounding).',
        images: [
          {
            src: CAPITAL_EFFICIENCY_IMG,
            alt: 'BTC/USD — Capital efficiency: Annualized return (CAGR) — All 369.73%, Long 352.70%, Short 348.63%',
            caption:
              'BTC/USD · Capital efficiency — Annualized return (CAGR) All / Long / Short',
          },
          {
            src: METRICS_IMG,
            alt: 'BTC/USD — Return details, Trades analysis, Equity run-ups & drawdowns (initial capital $10K, compounding)',
            caption:
              'BTC/USD · Full backtest metrics panel — Return details, Trades analysis, Equity run-ups & drawdowns',
          },
        ],
      },
      {
        icon: 'interpretation',
        heading: '4. Interpretation of the results',
        body: [
          'The Compound Annual Growth Rate (CAGR) stands at 369.73%, expressing the theoretical average annual growth rate of capital over the tested period, taking compound growth into account. CAGR is one of the most widely recognized metrics for evaluating investment strategies because it enables comparable performance assessment regardless of the backtest length.',
          'In parallel, the algorithm completed 69 total trades, of which 66.7% were profitable. This ratio indicates a strong ability to identify dominant market trends and to limit false signals.',
          'The average trade duration of 81 days confirms that the strategy is not designed for frequent position turnover or short-term high-frequency trading. Instead, the Momentum Volatility ST Indicator is designed to identify and follow the significant medium- and long-term movements of the market, allowing strong trends to fully develop before a new signal is produced.',
        ],
        metricCards: [
          { label: 'Annualized Return (CAGR)', value: '369.73%', accent: 'emerald' },
          { label: 'Winning Trades', value: '66.7%', accent: 'cyan' },
          { label: 'Total Trades', value: '69', accent: 'sky' },
          { label: 'Average Trade Duration', value: '81 Days', accent: 'violet' },
        ],
      },
      {
        icon: 'methodology',
        heading: '5. Scientific evaluation approach',
        body: [
          'The evaluation of the Momentum Volatility ST Indicator is based on objective quantitative criteria — not on isolated winning trades or short periods of high performance. The use of multi-year historical data, combined with statistical reliability and risk-management metrics, allows an assessment of the algorithm\u2019s stability, consistency, and adaptability across different market regimes.',
          'The Heliophant philosophy is that long-term success does not stem from predicting every individual market move, but from the systematic application of a disciplined and scientifically grounded methodology that seeks to optimize the relationship between return and assumed risk.',
        ],
      },
      {
        icon: 'note',
        heading: '6. Note',
        body: [
          'The metrics presented above are derived from historical simulations (backtesting) and are used exclusively to evaluate the statistical behavior of the algorithm. Historical performance does not constitute a guarantee of comparable future results, as the behavior of financial markets is influenced by factors that change over time.',
        ],
      },
    ],
    ctaTitle: 'See the signals in real conditions',
    ctaDesc:
      'The Momentum Volatility ST Indicator is available to Basic Plan subscribers via TradingView.',
    ctaBtn: 'Get access',
  },
};

const ICONS = {
  intro: ShieldAlert,
  btc: Coins,
  metrics: BarChart3,
  interpretation: Activity,
  methodology: FlaskConical,
  note: Info,
  cta: Target,
};

const MetricsTable = ({ headers, rows }) => (
  <div
    className="my-5 overflow-hidden rounded-xl border border-white/10 bg-white/[0.02]"
    data-testid="risk-metrics-table"
  >
    <table className="w-full text-left">
      <thead className="bg-white/[0.03] text-xs uppercase tracking-widest text-cyan-400">
        <tr>
          {headers.map((h) => (
            <th key={h} className="px-4 py-3 md:px-6 md:py-4 font-semibold">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-white/5 text-white/85">
        {rows.map(([metric, result]) => (
          <tr key={metric} data-testid={`risk-metric-row-${metric.toLowerCase().replace(/\s+/g, '-')}`}>
            <td className="px-4 py-3 md:px-6 md:py-4">{metric}</td>
            <td className="px-4 py-3 md:px-6 md:py-4 font-mono text-cyan-300">{result}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ACCENT_STYLES = {
  emerald: {
    glow: 'from-emerald-500/20 to-emerald-500/0',
    border: 'border-emerald-400/30',
    value: 'text-emerald-300',
    dot: 'bg-emerald-400',
  },
  cyan: {
    glow: 'from-cyan-500/20 to-cyan-500/0',
    border: 'border-cyan-400/30',
    value: 'text-cyan-300',
    dot: 'bg-cyan-400',
  },
  sky: {
    glow: 'from-sky-500/20 to-sky-500/0',
    border: 'border-sky-400/30',
    value: 'text-sky-300',
    dot: 'bg-sky-400',
  },
  violet: {
    glow: 'from-violet-500/20 to-violet-500/0',
    border: 'border-violet-400/30',
    value: 'text-violet-300',
    dot: 'bg-violet-400',
  },
};

const MetricCards = ({ cards }) => (
  <div
    className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
    data-testid="risk-metric-cards"
  >
    {cards.map((c) => {
      const style = ACCENT_STYLES[c.accent] || ACCENT_STYLES.cyan;
      return (
        <div
          key={c.label}
          className={`relative overflow-hidden rounded-xl border ${style.border} bg-white/[0.02] p-5 md:p-6 transition-transform duration-300 hover:-translate-y-1 hover:bg-white/[0.04]`}
          data-testid={`risk-metric-card-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        >
          <div
            className={`pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-radial ${style.glow} blur-2xl opacity-70`}
            aria-hidden="true"
          />
          <div className="flex items-center gap-2 mb-3">
            <span
              className={`inline-block h-2 w-2 rounded-full ${style.dot}`}
              aria-hidden="true"
            />
            <p className="text-white/60 text-xs uppercase tracking-widest font-medium">
              {c.label}
            </p>
          </div>
          <p
            className={`font-mono text-3xl md:text-4xl font-bold ${style.value} leading-none`}
          >
            {c.value}
          </p>
        </div>
      );
    })}
  </div>
);

const RiskBehaviorPage = () => {
  const { language } = useLanguage();
  const t = CONTENT[language] || CONTENT.en;

  return (
    <div
      className="min-h-screen bg-[#050811] text-white"
      data-testid="risk-behavior-page"
    >
      <Navbar />

      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <nav
            className="flex items-center text-sm text-white/50 mb-6"
            aria-label="breadcrumb"
          >
            <Link to="/" className="hover:text-white/80">
              {t.breadcrumb_home}
            </Link>
            <ChevronRight size={14} className="mx-2" />
            <span>{t.breadcrumb_features}</span>
            <ChevronRight size={14} className="mx-2" />
            <span className="text-white/80">{t.title}</span>
          </nav>

          {/* Header */}
          <div className="inline-block px-3 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-5">
            <span className="text-cyan-400 text-xs font-semibold uppercase tracking-widest">
              {t.kicker}
            </span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-3 leading-tight"
            data-testid="risk-behavior-title"
          >
            {t.title}
          </h1>
          <p className="text-lg text-white/70 mb-16">{t.subtitle}</p>

          {/* Sections */}
          <div className="space-y-14">
            {t.sections.map((s) => {
              const Icon = ICONS[s.icon] || ShieldAlert;
              return (
                <section
                  key={s.heading}
                  className="scroll-mt-24"
                  data-testid={`risk-section-${s.icon}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center">
                      <Icon className="text-cyan-400" size={20} />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-semibold">
                      {s.heading}
                    </h2>
                  </div>

                  {s.subheading && (
                    <p className="text-cyan-400/90 text-sm uppercase tracking-widest mt-1 mb-1">
                      {s.subheading}
                    </p>
                  )}
                  {s.highlight && (
                    <p
                      className="text-white text-xl md:text-2xl font-semibold mb-4"
                      data-testid={`risk-highlight-${s.icon}`}
                    >
                      {s.highlight}
                    </p>
                  )}

                  <div className="space-y-3 text-white/80 leading-relaxed">
                    {s.body && s.body.map((para) => <p key={para.slice(0, 30)}>{para}</p>)}
                  </div>

                  {s.table && <MetricsTable {...s.table} />}

                  {s.metricCards && <MetricCards cards={s.metricCards} />}

                  {s.note && (
                    <p className="text-white/60 text-sm italic mt-4">{s.note}</p>
                  )}

                  {s.images && s.images.length > 0 && (
                    <div className="mt-6 space-y-6">
                      {s.images.map((img) => (
                        <figure
                          key={img.src}
                          className="bg-white rounded-xl overflow-hidden border border-white/10"
                          data-testid={`risk-image-${img.src.split('/').pop().split('_')[0]}`}
                        >
                          <img
                            src={img.src}
                            alt={img.alt}
                            loading="lazy"
                            className="w-full h-auto block"
                          />
                          <figcaption className="text-white/70 text-xs md:text-sm px-4 py-3 border-t border-white/5 text-center bg-[#050811]">
                            {img.caption}
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>

          {/* CTA */}
          <div
            className="mt-20 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-2xl p-8 md:p-10 text-center"
            data-testid="risk-behavior-cta"
          >
            <h3 className="text-2xl md:text-3xl font-semibold mb-3">
              {t.ctaTitle}
            </h3>
            <p className="text-white/70 mb-6 max-w-xl mx-auto">{t.ctaDesc}</p>
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold px-8"
              data-testid="risk-behavior-cta-btn"
            >
              <Link to="/#pricing">{t.ctaBtn}</Link>
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RiskBehaviorPage;
